import type {
    MapData,
    MapDataHeroAction,
    MapDataHeroActionIconCategory,
    MapDataLocationFeature,
} from '@repo/types/maps/map-types';
import { getPinImageUrl } from '@repo/utility/image-urls';
import type {
    HeroActionGeneration,
    HeroActionMutationResponse,
    HeroActionPin,
    HeroActionPreviewResponse,
} from '@repo/wemod-api-client/maps-api-client';
import { buildCategoryMap, buildSubcategoryMap } from '../../../checklist/checklist-utils';
import { HERO_ACTION_ICON_OPTIONS } from './constants';
import type { HeroActionEditorPinDetails, HeroActionManualEditorState } from './types';

export function delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
}

export function isTerminalGenerationState(state: HeroActionGeneration['state']): boolean {
    return state === 'completed' || state === 'failed' || state === 'timed_out' || state === 'canceled';
}

export function getGenerationButtonLabel(status: HeroActionGeneration | null): string {
    return status?.state === 'queued' ? 'Queued' : 'Generating';
}

export function getGenerationStatusText(status: HeroActionGeneration): string {
    if (status.state === 'queued') {
        return 'Queued. Checking again in 5 seconds.';
    }

    if (status.state === 'running') {
        return 'Generating. Checking again in 5 seconds.';
    }

    if (status.state === 'canceled') {
        return status.message ?? 'Generation canceled.';
    }

    return status.error ?? status.message ?? 'Generation finished.';
}

export function parsePinExternalIds(value: string): string[] {
    return Array.from(
        new Set(
            value
                .split(/[\s,]+/)
                .map(pin => pin.trim())
                .filter(Boolean),
        ),
    );
}

export function formatGroupedPinIds(groupedPins: Readonly<Record<string, ReadonlyArray<string>>>): string {
    return Object.values(groupedPins)
        .flatMap(pins => [...pins])
        .join('\n');
}

export function formatPinIds(pinIds: ReadonlyArray<string>): string {
    return pinIds.join('\n');
}

export function getManualEditorState(heroAction: MapDataHeroAction): HeroActionManualEditorState {
    return {
        name: heroAction.name,
        description: heroAction.description ?? '',
        iconCategory: heroAction.iconCategory,
        priority: String(heroAction.priority),
        resultPins: formatGroupedPinIds(heroAction.resultPins),
        triggerPins: formatGroupedPinIds(heroAction.triggerPins),
    };
}

export function areManualEditorStatesEqual(a: HeroActionManualEditorState, b: HeroActionManualEditorState): boolean {
    return (
        a.name === b.name &&
        a.description === b.description &&
        a.iconCategory === b.iconCategory &&
        a.priority === b.priority &&
        a.resultPins === b.resultPins &&
        a.triggerPins === b.triggerPins
    );
}

export function reorder<T>(items: ReadonlyArray<T>, fromIndex: number, toIndex: number): T[] {
    const next = [...items];
    const [item] = next.splice(fromIndex, 1);
    if (item === undefined) return next;
    next.splice(toIndex, 0, item);
    return next;
}

export function buildMapPinDetails(
    mapData: MapData | null | undefined,
    mapExternalId: string | undefined,
    currentMapExternalId: string | undefined,
): Map<string, HeroActionEditorPinDetails> {
    const details = new Map<string, HeroActionEditorPinDetails>();
    if (!mapData) return details;

    const mapLabel = mapExternalId
        ? getMapLabel(mapExternalId, currentMapExternalId, mapData.metadata.name)
        : undefined;
    const subcategoryMap = buildSubcategoryMap(mapData);
    const categoryMap = buildCategoryMap(mapData);
    for (const feature of mapData.geojson.features) {
        if (feature.geometry.type !== 'Point') continue;
        const pinFeature = feature as MapDataLocationFeature;
        const subcategory = subcategoryMap.get(pinFeature.properties.subcategoryExternalId);
        const category = subcategory ? categoryMap.get(subcategory.categoryExternalId) : undefined;
        const title = pinFeature.properties.title || subcategory?.title || pinFeature.properties.externalId;
        const subtitle = [mapLabel, category?.title, subcategory?.title].filter(Boolean).join(' · ');

        details.set(pinFeature.properties.externalId, {
            title,
            subtitle: subtitle || undefined,
            imageUrl: getPinImageUrl(pinFeature.properties.imageId, pinFeature.properties.imagePath) ?? null,
            mapExternalId,
        });
    }

    return details;
}

export function getHeroActionMapExternalIds(heroAction: MapDataHeroAction): string[] {
    return Array.from(new Set([...Object.keys(heroAction.resultPins), ...Object.keys(heroAction.triggerPins)]));
}

export function buildHeroActionPinDetails(
    heroAction: MapDataHeroAction | null,
    currentMapExternalId: string | undefined,
): Map<string, HeroActionEditorPinDetails> {
    const details = new Map<string, HeroActionEditorPinDetails>();
    if (!heroAction) return details;

    for (const groupedPins of [heroAction.resultPins, heroAction.triggerPins]) {
        for (const [mapExternalId, pinIds] of Object.entries(groupedPins)) {
            for (const pinId of pinIds) {
                if (details.has(pinId)) continue;
                details.set(pinId, {
                    title: pinId,
                    subtitle: getMapLabel(mapExternalId, currentMapExternalId),
                    mapExternalId,
                });
            }
        }
    }

    return details;
}

export function buildPreviewPinDetails(
    preview: HeroActionPreviewResponse | null,
): Map<string, HeroActionEditorPinDetails> {
    const details = new Map<string, HeroActionEditorPinDetails>();
    if (!preview) return details;

    for (const pin of [...preview.preview.pins.result, ...preview.preview.pins.trigger]) {
        if (details.has(pin.externalId)) continue;

        const subtitle = [pin.gameMapName, pin.subcategory].filter(Boolean).join(' · ');
        details.set(pin.externalId, {
            title: pin.title || pin.externalId,
            subtitle: subtitle || pin.gameMapExternalId,
            mapExternalId: pin.gameMapExternalId,
        });
    }

    return details;
}

export function getMapLabel(mapExternalId: string, currentMapExternalId: string | undefined, mapName?: string): string {
    const label = mapName || mapExternalId;
    return mapExternalId === currentMapExternalId ? `${label} (current)` : label;
}

export function groupManualPinIdsForPreview(
    pinIds: ReadonlyArray<string>,
    originalGroupedPins: Readonly<Record<string, ReadonlyArray<string>>>,
    currentMapExternalId: string,
    pinMapById: ReadonlyMap<string, string>,
): Record<string, string[]> {
    const originalMapByPinId = new Map<string, string>();
    for (const [mapExternalId, groupedPinIds] of Object.entries(originalGroupedPins)) {
        for (const pinId of groupedPinIds) {
            originalMapByPinId.set(pinId, mapExternalId);
        }
    }

    return pinIds.reduce<Record<string, string[]>>((grouped, pinId) => {
        const mapExternalId = pinMapById.get(pinId) ?? originalMapByPinId.get(pinId) ?? currentMapExternalId;
        grouped[mapExternalId] ??= [];
        grouped[mapExternalId]?.push(pinId);
        return grouped;
    }, {});
}

export function mapSavedHeroActionToMapDataHeroAction(
    response: HeroActionMutationResponse,
    language: string,
): MapDataHeroAction {
    return {
        externalId: response.heroAction.externalId,
        name: translationForLanguage(response.heroAction.name, language),
        description: translationForLanguage(response.heroAction.description, language),
        iconCategory: toHeroActionIconCategory(response.heroAction.iconCategory),
        priority: response.heroAction.priority,
        isApproved: response.heroAction.isApproved,
        resultPins: groupHeroActionPinsByMap(response.preview.pins.result),
        triggerPins: groupHeroActionPinsByMap(response.preview.pins.trigger),
    };
}

function groupHeroActionPinsByMap(pins: ReadonlyArray<HeroActionPin>): Record<string, string[]> {
    return pins.reduce<Record<string, string[]>>((grouped, pin) => {
        grouped[pin.gameMapExternalId] ??= [];
        grouped[pin.gameMapExternalId]?.push(pin.externalId);
        return grouped;
    }, {});
}

function translationForLanguage(translations: Record<string, string>, language: string): string {
    return translations[language] ?? translations.en ?? Object.values(translations)[0] ?? '';
}

export function toHeroActionIconCategory(value: string): MapDataHeroActionIconCategory {
    return HERO_ACTION_ICON_OPTIONS.some(option => option.value === value)
        ? (value as MapDataHeroActionIconCategory)
        : 'loot_gear';
}

export function getCurrentMapResultPinIds(
    action: MapDataHeroAction | null,
    mapExternalId: string | null | undefined,
): string[] {
    if (!action || !mapExternalId) {
        return [];
    }

    return [...(action.resultPins[mapExternalId] ?? [])];
}
